import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CircleUserRound, ExternalLink } from "lucide-react";
import { DownArrowIcon } from "@/assets/icons/DownArrowIcon";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useAuth } from "@/shared/providers/AuthProvider";
import SubMenu from "./SubMenu";
import { MEGA_MENU_IDS, NavMenuProps, NavItem } from "./types";

const NavMenu: React.FC<NavMenuProps> = ({
  title,
  menuId,
  items,
  isUserMenu = false,
  openMenu,
  isRouteActive,
  onToggle,
  onClose,
}) => {
  const { hasPermission, hasPermissions } = usePermissions();
  const { user } = useAuth();
  const isMegaMenu = MEGA_MENU_IDS.has(menuId);
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * handleNavLinkClick – called by every internal <Link> in the menu.
   *
   * When the user is already on the target path (same-route click), we
   * invalidate ALL active React Query queries so every page that uses
   * React Query will automatically refetch its data.  This is the single
   * common place for the "refresh on same-route nav" behaviour – no
   * per-page wiring is required.
   */
  const handleNavLinkClick = (
    e: React.MouseEvent,
    targetPath: string,
    closeMenu = true,
  ) => {
    if (location.pathname === targetPath) {
      e.preventDefault();
      // Give the user a "fresh start" — same experience as navigating to the
      // page for the very first time:
      //   1. Remove all cached query data so queries run with no stale data.
      //   2. Navigate to the bare path (dropping all search params / filters).
      //   3. Pass a unique _mountKey in state so the Layout can key the page
      //      content and force a full remount, resetting all local React state
      //      (filters, sort, pagination, etc.) back to defaults.
      queryClient.removeQueries();
      navigate(targetPath, {
        replace: true,
        state: { _mountKey: Date.now() },
      });
    }
    if (closeMenu) {
      onClose();
    }
  };

  const getMenuItems = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"], .submenu-container > button') || []
    );

  useEffect(() => {
    if (openMenu !== menuId) {
      setFocusedIndex(null);
    }
  }, [openMenu, menuId]);

  useEffect(() => {
    if (openMenu === menuId && focusedIndex !== null) {
      const menuItems = getMenuItems();
      if (menuItems.length === 0) {
        return;
      }

      const normalizedIndex = focusedIndex < 0 ? menuItems.length - 1 : focusedIndex % menuItems.length;
      menuItems[normalizedIndex]?.focus();
    }
  }, [focusedIndex, openMenu, menuId]);

  const filterItemsByPermission = (items: NavItem[]): NavItem[] => {
    return items
      .filter((item) => {
        // Check user type restriction first
        if (item.allowedUserTypes && item.allowedUserTypes.length > 0) {
          const userType = user?.subcontractor ?? 0; // Default to Normal (0) if not set
          if (!item.allowedUserTypes.includes(userType)) {
            return false;
          }
        }
        
        // Check permissions
        if (!item.permission && !item.permissions) {
          return true;
        }
        if (item.permission) {
          return item.allowWithoutPermission || hasPermission(item.permission);
        }
        if (item.permissions) {
          return hasPermissions(item.permissions, false);
        }
        return true;
      })
      .map((item) => {
        const filteredSubmenu = item.submenu ? filterItemsByPermission(item.submenu) : undefined;
        return {
          ...item,
          ...(filteredSubmenu !== undefined && { submenu: filteredSubmenu }),
        };
      })
      .filter((item) => {
        if (item.submenu && item.submenu.length === 0) {
          return false;
        }
        return true;
      });
  };

  const filteredItems = filterItemsByPermission(items);

  if (filteredItems.length === 0 && !isUserMenu) {
    return null;
  }

  const megaMenuGridConfig: Record<string, string> = {
    operations: "170px 180px 130px 150px",
    admin: "120px 185px 165px 270px",
    tools: "160px 130px 200px 170px",
    system: "150px 155px 175px 200px 200px",
  };

  const allGridColumns = megaMenuGridConfig[menuId]?.split(" ") || [];
  const visibleItemKeys = new Set(filteredItems.map((item) => item.key));
  const visibleColumns = items
    .map((originalItem, index) => ({ key: originalItem.key, index }))
    .filter((item) => visibleItemKeys.has(item.key))
    .map(({ index }) => allGridColumns[index])
    .filter(Boolean);

  const gridColumns = visibleColumns.join(" ");

  const calculateMegaMenuWidth = (cols: string) => {
    if (!cols) return "auto";
    const columnWidths = cols.split(" ").map((c) => parseInt(c, 10));
    if (columnWidths.some(isNaN)) return "auto";

    const gap = 56;
    const padding = 24 * 2;
    const totalWidth =
      columnWidths.reduce((acc, width) => acc + width, 0) +
      (columnWidths.length > 0 ? (columnWidths.length - 1) * gap : 0) +
      padding;
    return `${totalWidth}px`;
  };

  const megaMenuWidth = isMegaMenu ? calculateMegaMenuWidth(gridColumns) : "auto";

  const renderMenuLabel = (item: NavItem, compact = false) => {
    const Icon = item.icon;

    return (
      <span className={`nav-item-label ${compact ? "nav-item-label-compact" : ""}`}>
      {Icon && (
        <span className="nav-item-icon" aria-hidden="true">
          <Icon size={15} />
        </span>
      )}
      <span className="nav-item-text">{item.label}</span>
      {item.external && (
        <ExternalLink size={13} className="nav-item-external" aria-hidden="true" />
      )}
    </span>
    );
  };

  const moveFocus = (direction: "next" | "prev") => {
    const menuItems = getMenuItems();
    if (menuItems.length === 0) {
      return;
    }

    const activeIndex = menuItems.findIndex((item) => item === document.activeElement);
    const fallbackIndex = direction === "next" ? -1 : 0;
    const currentIndex = activeIndex >= 0 ? activeIndex : (focusedIndex ?? fallbackIndex);
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % menuItems.length
        : (currentIndex - 1 + menuItems.length) % menuItems.length;

    setFocusedIndex(nextIndex);
    menuItems[nextIndex]?.focus();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {

    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        onToggle(menuId);
        if (openMenu !== menuId) {
          setFocusedIndex(0);
        }
        break;
      case "Escape":
        if (openMenu === menuId) {
          onClose();
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (openMenu !== menuId) {
          onToggle(menuId);
          setFocusedIndex(0);
        } else {
          moveFocus("next");
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (openMenu !== menuId) {
          onToggle(menuId);
          setFocusedIndex(-1);
        } else {
          moveFocus("prev");
        }
        break;
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        onClose();
        break;
      case "ArrowDown":
        event.preventDefault();
        moveFocus("next");
        break;
      case "ArrowUp":
        event.preventDefault();
        moveFocus("prev");
        break;
      case "Home": {
        const menuItems = getMenuItems();
        if (menuItems.length === 0) {
          break;
        }
        event.preventDefault();
        setFocusedIndex(0);
        menuItems[0]?.focus();
        break;
      }
      case "End": {
        const menuItems = getMenuItems();
        if (menuItems.length === 0) {
          break;
        }
        event.preventDefault();
        const lastIndex = menuItems.length - 1;
        setFocusedIndex(lastIndex);
        menuItems[lastIndex]?.focus();
        break;
      }
    }
  };

  return (
    <div className="navbar-menu-container" role="menu">
      <button
        onClick={() => onToggle(menuId)}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={openMenu === menuId}
        aria-haspopup="true"
        aria-controls={`${menuId}-menu`}
        aria-label={isUserMenu ? `User menu: ${title}` : `${title} menu`}
        role="menuitem"
        className={`nav-top-trigger flex items-center text-on-primary font-normal gap-2 px-4 py-2 ${openMenu === menuId || (!isUserMenu && isRouteActive) ? "active" : ""}`}
      >
        {isUserMenu && <CircleUserRound />}
        <span>{title}</span>
        <DownArrowIcon aria-hidden="true" />
      </button>
      {openMenu === menuId && (
        <div
          ref={menuRef}
          id={`${menuId}-menu`}
          onKeyDown={handleMenuKeyDown}
          className={`navbar-menu open navbar-menu-${menuId} ${isMegaMenu ? "mega" : ""} ${menuId === "operations" ? "operations-mega-menu" : ""} ${menuId === "admin" ? "admin-mega-menu" : ""} ${menuId === "tools" ? "tools-mega-menu" : ""} ${menuId === "system" ? "system-mega-menu" : ""}`}
          role="menu"
          aria-label={`${title} menu items`}
          style={{ width: megaMenuWidth }}
        >
          {isMegaMenu ? (
            <div
              className="mega-menu-grid"
              style={{ gridTemplateColumns: gridColumns }}
            >
              {filteredItems.map((item) => (
                <div key={item.key} className="min-w-0">
                  <div className="mega-menu-section-title">{item.label}</div>
                  <div className="flex flex-col gap-0">
                    {item.submenu?.map((subItem) => (
                      subItem.external ? (
                        <a
                          key={subItem.key}
                          href={subItem.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onClose}
                          className="mega-menu-link"
                          role="menuitem"
                        >
                          {renderMenuLabel(subItem)}
                        </a>
                      ) : (
                        <Link
                          key={subItem.key}
                          to={subItem.to}
                          onClick={(e) => handleNavLinkClick(e, subItem.to)}
                          className="mega-menu-link"
                          role="menuitem"
                        >
                          {renderMenuLabel(subItem)}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            filteredItems.map((item) =>
                item.divider ? (
                  <div key={item.key} className="border-t border-border my-2" />
                ) : item.versionInfo ? (
                  <div
                    key={item.key}
                    className="flex items-center gap-2 px-4 py-2 text-sm select-none"
                    aria-label="Application version information"
                  >
                    {item.version && (
                      <span className="text-text-muted">{item.version}</span>
                    )}
                    {item.releaseNotesUrl && (
                      <a
                        href={item.releaseNotesUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-tableBlueColor hover:underline"
                        role="menuitem"
                      >
                        Release notes
                      </a>
                    )}
                  </div>
                ) : item.submenu ? (
                <SubMenu key={item.key} title={item.label!} closeParentMenu={onClose}>
                  {item.submenu.map((subItem) =>
                    subItem.external ? (
                      <a
                        key={subItem.key}
                        href={subItem.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-text hover:bg-surface-alt"
                        role="menuitem"
                      >
                        {renderMenuLabel(subItem, true)}
                      </a>
                    ) : (
                      <Link
                        key={subItem.key}
                        to={subItem.to}
                        onClick={(e) => handleNavLinkClick(e, subItem.to, false)}
                        className="block px-4 py-2 text-sm text-text hover:bg-surface-alt"
                        role="menuitem"
                      >
                        {renderMenuLabel(subItem, true)}
                      </Link>
                    )
                  )}
                </SubMenu>
              ) : item.onClick ? (
                <button
                  key={item.key}
                  onClick={() => {
                    item.onClick?.();
                    onClose();
                  }}
                  className={`block w-full text-left px-4 py-2 text-sm ${item.key === "user-signout" ? "text-error" : "text-text"} hover:bg-surface-alt`}
                  role="menuitem"
                >
                  {renderMenuLabel(item, true)}
                </button>
              ) : (
                item.external ? (
                      <a
                        key={item.key}
                        href={item.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-text hover:bg-surface-alt"
                        role="menuitem"
                      >
                        {renderMenuLabel(item, true)}
                      </a>
                    ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={(e) => handleNavLinkClick(e, item.to)}
                  className={`block px-4 py-2 text-sm ${item.key === "user-signout" ? "text-error" : "text-text"} hover:bg-surface-alt`}
                  role="menuitem"
                >
                  {renderMenuLabel(item, true)}
                </Link>
                    )
              )
            )
          )}
        </div>
      )}
    </div>
  );
};

export default NavMenu;
